import { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import { debounce } from 'lodash';
import { useToast } from '../contexts/ToastContext';
import { chatConfig } from '../config/chatConfig';
import { getTokenCount } from '../helpers/chatHelper';
import { useAuth } from '../contexts/AuthContext';
import { useChatContext } from '../contexts/AppDataContext';
import { useDraft } from './useDraft';
import { useWindow } from '../contexts/WindowContext';
import { useAttachmentContext } from '../contexts/AttachmentContext';
import { useUndoRedo } from './useUndoRedo'; // Now optimized
import { isLikelyCode } from '../utils/codeDetection';

export function useInputTextArea() {
  const auth = useAuth();
  const { currentChatId } = useChatContext();
  const { windowWidth, openCodeDetectionModal } = useWindow();
  const toast = useToast();
  const { textFiles, imageFiles, processFiles, isTextAttachmentViewOpen } = useAttachmentContext();
  const [input, setInput] = useState<string>('');
  const [isAutoFocused, setIsAutoFocused] = useState(false);
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [needsCursorRestore, setNeedsCursorRestore] = useState(false); // NEW: For deferred cursor sets without every-keystroke overhead
  const inputRef = useRef<string>(input);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const cursorPositionRef = useRef<number | null>(null);
  const handleSubmitRef = useRef((e: React.FormEvent) => {});

  const {
    currentValue: undoRedoValue,
    recordChange,
    undo,
    redo,
    resetHistory: resetUndoRedoHistory,
    updateCurrentState: updateUndoRedoState,
  } = useUndoRedo<string>({ initialValue: '' });

  useEffect(() => {
    if (input !== undoRedoValue) {
      updateUndoRedoState(input, cursorPositionRef.current);
    }
  }, [input, updateUndoRedoState, undoRedoValue]); // Runs every keystroke but cheap (string ===)

  // Deferred cursor restore — runs synchronously after DOM commit so focus (set
  // imperatively elsewhere) has already landed before we set selection.
  useLayoutEffect(() => {
    if (needsCursorRestore && textareaRef.current && cursorPositionRef.current !== null) {
      const el = textareaRef.current;
      const pos = cursorPositionRef.current;
      if (document.activeElement !== el) {
        el.focus();
      }
      el.selectionStart = pos;
      el.selectionEnd = pos;
      setNeedsCursorRestore(false);
    }
  }, [needsCursorRestore]);

  const shouldAutoFocus = useCallback(() => {
    if (windowWidth <= 800) return false;
    if (isTextAttachmentViewOpen) return false;
    return true;
  }, [windowWidth, isTextAttachmentViewOpen]);

  const focusInput = useCallback(() => {
    if (shouldAutoFocus()) {
      setIsAutoFocused(true);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [shouldAutoFocus]);

  const handleTextareaClick = useCallback(() => {
    setIsAutoFocused(true);
  }, []);

  useDraft(currentChatId, input, setInput, inputRef, textareaRef);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  // Drive focus imperatively whenever the shouldAutoFocus conditions flip.
  // `autoFocus` is a mount-only React prop, so state alone won't re-focus after
  // e.g. closing the attachment view or resizing from mobile → desktop.
  useEffect(() => {
    const shouldFocus = shouldAutoFocus();
    setIsAutoFocused(shouldFocus);
    const el = textareaRef.current;
    if (!el) return;
    if (shouldFocus) {
      if (document.activeElement !== el) el.focus();
    } else if (document.activeElement === el) {
      el.blur();
    }
  }, [shouldAutoFocus]);

  const randomPlaceholder = useMemo(() => {
    return chatConfig.suggestions.chatPlaceholder[Math.floor(Math.random() * chatConfig.suggestions.chatPlaceholder.length)];
  }, []);

  const adjustTextareaHeightInternal = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.transition = 'none';
      textareaRef.current.style.height = '23px';
      const maxHeight = 350;
      const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
      void textareaRef.current.offsetHeight;
      textareaRef.current.style.transition = '';
    }
  }, []);

  const debouncedTokenCheck = useMemo(() => {
    return debounce((text: string) => {
      try {
        const tokenCount = getTokenCount(text);
        if (tokenCount > chatConfig.limits.tokens.MAX_TOKEN_COUNT * 0.9) {
          // Warning could be added here
        }
      } catch (error) {
        console.error('Error counting tokens:', error);
      }
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedTokenCheck.cancel();
    };
  }, [debouncedTokenCheck]);

  useLayoutEffect(() => {
    adjustTextareaHeightInternal();
  }, [input, adjustTextareaHeightInternal]);

  const resetHistory = useCallback(() => {
    resetUndoRedoHistory('');
    setInput('');
    inputRef.current = '';
    cursorPositionRef.current = null;
    if (textareaRef.current) {
      textareaRef.current.style.height = '23px';
    }
  }, [resetUndoRedoHistory]);

  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState) {
      setInput(previousState.value);
      inputRef.current = previousState.value;
      cursorPositionRef.current = previousState.cursorPosition;
      setNeedsCursorRestore(true);

      if (previousState.value.length <= chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
        setLimitExceeded(false);
      }
    }
  }, [undo]);

  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      setInput(nextState.value);
      inputRef.current = nextState.value;
      cursorPositionRef.current = nextState.cursorPosition;
      setNeedsCursorRestore(true);

      if (nextState.value.length > chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
        setLimitExceeded(true);
      } else {
        setLimitExceeded(false);
      }
    }
  }, [redo]);

  const applyPastedText = useCallback((pastedText: string) => {
    const currentContent = inputRef.current;
    const currentCursorPos = textareaRef.current?.selectionStart ?? currentContent.length;
    const textBeforeCursor = currentContent.slice(0, currentCursorPos);
    const textAfterCursor = currentContent.slice(currentCursorPos);

    let combinedText = textBeforeCursor + pastedText + textAfterCursor;
    let finalCursorPos = textBeforeCursor.length + pastedText.length;

    if (combinedText.length > chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
      const availableSpace = chatConfig.limits.tokens.MAX_MESSAGE_LENGTH - (textBeforeCursor.length + textAfterCursor.length);
      const trimmedPastedText = pastedText.slice(0, Math.max(0, availableSpace));
      combinedText = textBeforeCursor + trimmedPastedText + textAfterCursor;
      finalCursorPos = textBeforeCursor.length + trimmedPastedText.length;
      toast(`Pasted text was trimmed to fit character limit!`, 'error');
    }

    recordChange(combinedText, finalCursorPos);
    setInput(combinedText);
    inputRef.current = combinedText;
    cursorPositionRef.current = finalCursorPos;
    setNeedsCursorRestore(true);
  }, [toast, recordChange]);

  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const clipboardFiles = e.clipboardData.files;
    if (clipboardFiles && clipboardFiles.length > 0) {
      const simulatedEvent = { target: { files: clipboardFiles } } as unknown as React.ChangeEvent<HTMLInputElement>;
      processFiles(simulatedEvent);
      return;
    }
    const pastedText = e.clipboardData.getData('text');
    if (!pastedText) return;

    if (pastedText.length > 500 && auth.isAuthenticated && isLikelyCode(pastedText)) {
      openCodeDetectionModal(
        pastedText,
        () => applyPastedText(pastedText)
      );
      return;
    }

    applyPastedText(pastedText);
  }, [processFiles, openCodeDetectionModal, applyPastedText, auth.isAuthenticated]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    if (newValue.length > chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
      if (!limitExceeded) {
        setLimitExceeded(true);
        toast(`You've reached the character limit!`, 'error');
      }
      const cutValue = newValue.slice(0, chatConfig.limits.tokens.MAX_MESSAGE_LENGTH);
      const finalCursorPos = Math.min(cursorPosition, cutValue.length);

      setInput(cutValue);
      inputRef.current = cutValue;
      cursorPositionRef.current = finalCursorPos;
      updateUndoRedoState(cutValue, finalCursorPos);
      setNeedsCursorRestore(true); // NEW: For trimmed case
    } else {
      if (limitExceeded) {
        setLimitExceeded(false);
      }
      recordChange(newValue, cursorPosition);
      setInput(newValue);
      inputRef.current = newValue;
      cursorPositionRef.current = cursorPosition;
    }

    adjustTextareaHeightInternal();
    debouncedTokenCheck(newValue);
  }, [
    adjustTextareaHeightInternal,
    debouncedTokenCheck,
    toast,
    recordChange,
    updateUndoRedoState,
    limitExceeded
  ]); // Now stable (limitExceeded changes rarely)

  // FIXED: Use inputRef.current for stability; removed 'input' from deps
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleUndo();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleRedo();
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // FIXED: Use ref for current input value (avoids dep on 'input')
      const isDisabled =
        (!inputRef.current.trim() && !textFiles.length && !imageFiles.length) ||
        (auth.chatUsageData?.remaining !== null &&
         auth.chatUsageData?.remaining !== undefined &&
         auth.chatUsageData.remaining <= 0);

      if (!isDisabled) {
        handleSubmitRef.current(e);
      }
    }
  }, [
    handleUndo,
    handleRedo,
    // FIXED: Removed 'input'; kept rare-change deps
    textFiles.length,
    imageFiles.length,
    auth.chatUsageData?.remaining
  ]); // Now stable during typing

  const updateHandleSubmitInTextArea = useCallback((newHandleSubmit: (e: React.FormEvent) => void) => {
    handleSubmitRef.current = newHandleSubmit;
  }, []);

  const checkMobile = useCallback(() => {
    if (windowWidth < 800) {
      if (textareaRef.current) {
        textareaRef.current.blur();
      }
      setIsAutoFocused(false);
    }
  }, [windowWidth]);

  const restoreInput = useCallback((content: string) => {
    setInput(content);
    inputRef.current = content;
    resetUndoRedoHistory(content);
    cursorPositionRef.current = content.length;
    // The needsCursorRestore effect will handle focus + selection together.
    setNeedsCursorRestore(true);

    if (content.length <= chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
      setLimitExceeded(false);
    } else {
      setLimitExceeded(true);
    }
  }, [resetUndoRedoHistory]);
  
  const handleSuggestionClick = useCallback((_text: string) => {
    // No-op
  }, []);

  // NEW: Stable ref callback for internal attachment (no cursor set; handled by needsRestore)
  const internalRef = useCallback((el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (el) {
      adjustTextareaHeightInternal();
      // Re-apply focus on (re)mount if conditions still call for it. Covers
      // parent conditional rendering / key changes where React's mount-only
      // `autoFocus` prop wouldn't fire a second time.
      if (shouldAutoFocus() && document.activeElement !== el) {
        el.focus();
      }
      if (document.activeElement === el && cursorPositionRef.current !== null) {
        el.selectionStart = cursorPositionRef.current;
        el.selectionEnd = cursorPositionRef.current;
      }
    }
  }, [adjustTextareaHeightInternal, shouldAutoFocus]);

  // NEW: Props object for <textarea> (new on every input, but React diffs efficiently; no unmount)
  const textareaProps = useMemo(() => ({
    value: input,
    onChange: handleInputChange,
    onKeyDown: handleKeyDown,
    onClick: handleTextareaClick,
    placeholder: randomPlaceholder,
    'aria-label': 'Chat input',
    className: 'chat-input scrollable',
    // autoFocus is mount-only; initial mount value is all it's good for.
    // Ongoing focus is handled imperatively via the shouldAutoFocus effect
    // and internalRef so state changes actually move DOM focus.
    autoFocus: isAutoFocused,
    onPaste: handlePaste,
  }), [
    input,
    handleInputChange,
    handleKeyDown,
    handleTextareaClick,
    randomPlaceholder,
    isAutoFocused,
    handlePaste,
  ]);

  return {
    input,
    setInput,
    inputRef,
    textareaRef,
    restoreInput,
    focusInput,
    cursorPositionRef,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    // REMOVED: MemoizedTextarea (replaced with props/ref)
    textareaProps, // NEW
    internalRef, // NEW
    handleSuggestionClick,
    adjustTextareaHeight: adjustTextareaHeightInternal,
    updateHandleSubmitInTextArea,
    handleUndo,
    handleRedo,
    resetHistory,
    checkMobile,
  };
}
